import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCGrid, RCCHookToast, RCCServicesApi } from "react-common-components";
import ListCards from "src/components/ListCards";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import CardActions from "./components/CardActions";
import ListActions from "./components/ListActions";
import { ActionSituationFilter, IActionShowPostponementRef, IListActions, IListOfActions, IRefActionsPanelModalAddChangeDelete, IRefActionsProgressModal } from "./interface";
import ModalCancel, { IActionCancelRef } from "./components/modalCancel";
import CommentsModal, { IActionCommentsRef } from "./components/modalComments";
import ModalHistory, { IActionHistoryRef } from "./components/modalHistory";
import ModalPostponement, { IActionPostponementRef } from "./components/modalPostponement";
import ModalsSubactions, { IRefModalSubaction } from "./components/modalsSubactions";
import ModalProgress from "./components/modalProgress";
import ModalAddChangeDelete from "./components/modalAddChangeDelete";
import ModalEfficiency, { IActionEfficiencyRef } from "./components/modalEfficiency";
import executeQueryListOfActions from "./executeQueryListOfActions";
import OptionsOfActions from "./components/OptionsOfActions";
import ModalRelateSprint, { IRefModalRelateSprint } from "./components/modalRelateSprint";
import ModalShowPostponements from "./components/modalShowPostponements";

export interface IRefListOfActions{
	executeQuery: () => Promise<void>;
	getActions:()=>IListActions[];
}

interface IHeader{
	showTotal?: boolean;
	button?: React.ReactNode;
	listLength: number
}

const Header: React.FC<IHeader> = ({
	showTotal,
	button,
	listLength
}) => {
	const {translate} = useTranslation();
	return (<>
		<RCCGrid
			container
			item
			justifyContent="space-between"
		>
			{showTotal && (
				<Title color="purple" size="md">
					{translate("Total")}: {listLength} {listLength > 1 ? translate("ações") : translate("ação")}
				</Title>
			)}
			<CheckPermission path="/actions/panel" minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
				{button && button}
			</CheckPermission>
		</RCCGrid>
		<hr />
	</>

	);
};

const ListOfActions: React.ForwardRefRenderFunction<IRefListOfActions, IListOfActions> = ({
	layoutList,
	onCallbackAdd,
	onCallbackUpdate,
	onCallbackDelete,
	callBackChangeActionSprint,
	positionOptions = "left",
	filterList,
	filters,
	groupersSelect,
	groupers,
	header,
	layout,
	defaultActions
}, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { administrator, superUser, user } = useAuth();

	const modalsRef = useRef<IRefActionsPanelModalAddChangeDelete>(null);
	const modalsSubactionsRef = useRef<IRefModalSubaction>(null);
	const modalCommentsRef = useRef<IActionCommentsRef>(null);
	const modalProgressRef = useRef<IRefActionsProgressModal>(null);
	const modalHistoryRef = useRef<IActionHistoryRef>(null);
	const modalPostponementRef = useRef<IActionPostponementRef>(null);
	const modalCancelRef = useRef<IActionCancelRef>(null);
	const modalEfficiencyRef = useRef<IActionEfficiencyRef>(null);
	const modalSRelateSprint = useRef<IRefModalRelateSprint>(null);
	const modalShowPostponements = useRef<IActionShowPostponementRef>(null);
	
	const [loading, setLoading] = useState(!defaultActions);
	const [list, setList] = useState<IListActions[]>(defaultActions||[]);
	const [listLength, setListLegth] = useState(list.length);

	const onCallbackAddFunction = async () => {
		if(onCallbackAdd)
			onCallbackAdd();
		else
			await executeQuery();
	};

	const options = (row: IListActions) => (
		<OptionsOfActions 
			row={row}
			positionOptions={positionOptions}
			actionButtonDelete={() => {
				modalsRef.current?.actionButtonDelete(row);
			}}
			openModalActionCancel={() => {
				modalCancelRef.current?.openModalActionCancel(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`, () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
			}}
			openModalActionPostponement={() => {
				modalPostponementRef.current?.openModalActionPostponement(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`, row.inb001Dtfim , () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
			}}
			openModalAddOrChange={(isDisabled, isCopy) => {
				modalsRef.current?.openModalAddOrChange(row.inb001Id, isCopy ? () => onCallbackAddFunction() : () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao), undefined, undefined, undefined, undefined, undefined, isDisabled, isCopy);
			}}
			openModalAddSubaction={() => {
				modalsRef.current?.openModalAddOrChange(undefined, () => onCallbackAddFunction(), undefined, row.inb001Id);
			}}
			openModalChangeProgress={(isReopen: boolean) => {
				modalProgressRef.current?.openModalChangeProgress(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`, row.inb001PercConclusao , () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao), isReopen);
			}}
		/>
	);
	
	const onCallbackUpdateFunction = async (id: number, situation: string, percent: number) => {
		try{
			const response = await RCCServicesApi.getRequest({
				method: "Actions/InfosOfActionForList",
				urlParameters: {
					id
				}
			});
			const action = response.data as IListActions;

			setList((prevState) => prevState.map((glist) => {
				if(glist.inb001Id === id)
					return action;
				return glist;
			}));

			if(onCallbackUpdate)
				onCallbackUpdate(id, action, situation, percent);

		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar ação, favor atualizar a pagina"), addToast);
		}
	};

	const executeQuery = async () => {
		try{
			const listActions = await executeQueryListOfActions(groupersSelect, {
				...groupers
			}, filters);

			setList(listActions);
			setListLegth(listActions.length);
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar as ações"), addToast);
		}
	};

	const onCallbackDeleteFunction = (inb001Id: number, situation: string, prevPercent: number) => {
		setList((prevState) => prevState.filter((x) => x.inb001Id !== inb001Id));
		setListLegth((prevState) => prevState - 1);
		if(onCallbackDelete)
			onCallbackDelete(inb001Id, situation, prevPercent);
	};

	useImperativeHandle(ref,() => ({
		executeQuery,
		getActions:() => list
	}));

	if(loading)
		return <Loader />;

	if(!list || list.length === 0)
		return (
			<>
				{header && <Header listLength={listLength} button={header.button} showTotal={header.showTotal} />}
				<RCCAlert 
					title={translate("Atenção")}
					description={translate("Não foram encontrados dados")}
				/>
			</>
		);

	return (
		<>
			{header && <Header listLength={listLength} button={header.button} showTotal={header.showTotal} />}
			{layoutList === "card" ? (
				<ListCards 
					data={list}
					mapper={(action: any) => 
						<CardActions key={v4()} 
							layout="withProgressBarPredicted"
							data={action} 
							options={(row: IListActions) => options(row)} 
							openModalComments={(row: IListActions) => {
								if(!modalCommentsRef.current)
									return;
								modalCommentsRef.current.openModalActionComments(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
							}}
							openModalAttachments={(row: IListActions, showOnlyWithAttachments: boolean) => {
								if(!modalHistoryRef.current)
									return;

								modalHistoryRef.current.openModalActionHistory(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`, showOnlyWithAttachments,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao), 
									!(administrator || superUser || row.inb001Efa005Id2 === user.efa005Id || row.inb001Efa005Id === user.efa005Id) || (row.situation?.toString() === ActionSituationFilter.FINISHED || row.situation?.toString() === ActionSituationFilter.CANCELED));
							}}
							openModalSubactions={(row: IListActions) => {
								if(!modalsSubactionsRef.current)
									return;

								modalsSubactionsRef.current.openModalSubactions(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`, layoutList);
							}}
							openModalPostPonementConfirm={(row: IListActions) => {
								if(!modalPostponementRef.current)
									return;
								if(!(administrator || superUser || row.inb001Efa005Id2 === user.efa005Id)){
									addToast({
										title: translate("Erro"),
										description: translate("Apenas autoridade ou administrador pode confirmar a postergação"),
										color: "yellow"
									});
									return;
								}
								modalPostponementRef.current.openModalActionPostponementConfirm(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
							}}
							openModalCancelConfirm={(row: IListActions) => {
								if(!modalCancelRef.current)
									return;
								if(!(administrator || superUser || row.inb001Efa005Id2 === user.efa005Id)){
									addToast({
										title: translate("Erro"),
										description: translate("Apenas autoridade ou administrador pode confirmar o cancelamento"),
										color: "yellow"
									});
									return;
								}
								modalCancelRef.current.openModalActionCancelConfirm(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
							}}
							openModalEfficiency={(row: IListActions) => {
								if(!modalEfficiencyRef.current)
									return;
								modalEfficiencyRef.current.openModalActionEfficiency(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
							}}
							openModalChangeProgress={(row: IListActions) => {
								if(!modalProgressRef.current)
									return;
								if(!(administrator || superUser || row.inb001Efa005Id === user.efa005Id || row.inb001Efa005Id2 === user.efa005Id)){
									addToast({
										title: translate("Erro"),
										description: translate("Apenas autoridade, responsável ou administrador pode alterar o andamento"),
										color: "yellow"
									});
									return;
								}
								modalProgressRef.current?.openModalChangeProgress(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`, row.inb001PercConclusao , () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
							}}
							openModalShowPostponements={(row: IListActions) => {
								if(!modalProgressRef.current)
									return;
								modalShowPostponements.current?.openModalActionShowPostponements(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`);
							}}
						/>}
					filter={filterList ? (action) => action.situation === filterList : undefined} 
				/>
			): (
				<ListActions 
					data={list}
					layout={layout || "withProgressBarPredicted"}
					options={(row: IListActions) => options(row)}
					openModalComments={(row: IListActions) => {
						if(!modalCommentsRef.current)
							return;
						modalCommentsRef.current.openModalActionComments(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
					}}
					openModalSubactions={(row: IListActions) => {
						if(!modalsSubactionsRef.current)
							return;

						modalsSubactionsRef.current.openModalSubactions(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`, layoutList);
					}}
					openModalAttachments={(row: IListActions, showOnlyWithAttachments: boolean) => {
						if(!modalHistoryRef.current)
							return;
																	
						modalHistoryRef.current.openModalActionHistory(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`, showOnlyWithAttachments,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao), 
							!(administrator || superUser || row.inb001Efa005Id2 === user.efa005Id || row.inb001Efa005Id === user.efa005Id) || (row.situation?.toString() === ActionSituationFilter.FINISHED || row.situation?.toString() === ActionSituationFilter.CANCELED));
					}}
					openModalPostPonementConfirm={(row: IListActions) => {
						if(!modalPostponementRef.current)
							return;
						if(!(administrator || superUser || row.inb001Efa005Id2 === user.efa005Id)){
							addToast({
								title: translate("Erro"),
								description: translate("Apenas autoridade ou administrador pode confirmar a postergação"),
								color: "yellow"
							});
							return;
						}
						modalPostponementRef.current.openModalActionPostponementConfirm(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
					}}
					openModalCancelConfirm={(row: IListActions) => {
						if(!modalCancelRef.current)
							return;
						if(!(administrator || superUser || row.inb001Efa005Id2 === user.efa005Id)){
							addToast({
								title: translate("Erro"),
								description: translate("Apenas autoridade ou administrador pode confirmar o cancelamento"),
								color: "yellow"
							});
							return;
						}
						modalCancelRef.current.openModalActionCancelConfirm(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
					}}
					openModalEfficiency={(row: IListActions) => {
						if(!modalEfficiencyRef.current)
							return;
						modalEfficiencyRef.current.openModalActionEfficiency(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`,  () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
					}}
					openModalChangeProgress={(row: IListActions) => {
						if(!modalProgressRef.current)
							return;
						if(!(administrator || superUser || row.inb001Efa005Id === user.efa005Id || row.inb001Efa005Id2 === user.efa005Id)){
							addToast({
								title: translate("Erro"),
								description: translate("Apenas autoridade, responsável ou administrador pode alterar o andamento"),
								color: "yellow"
							});
							return;
						}
						modalProgressRef.current?.openModalChangeProgress(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`, row.inb001PercConclusao , () => onCallbackUpdateFunction(row.inb001Id, row.situation, row.inb001PercConclusao));
					}}
					openModalRelateSprint={(row: IListActions, title: string) => {
						if(!modalSRelateSprint.current)
							return;
						modalSRelateSprint.current?.openModalRelateSprint(row.inb001Id, groupers.grouperId3 ?? 0, groupers.grouperId2 ?? 0, `${row.inb001Codigo} - ${row.inb001Titulo}`, title);
					}}
					openModalShowPostponements={(row: IListActions) => {
						if(!modalShowPostponements.current)
							return;
						modalShowPostponements.current?.openModalActionShowPostponements(row.inb001Id, `${row.inb001Codigo} - ${row.inb001Titulo}`);
					}}
					filter={filterList ? (action) => action.situation === filterList : undefined} 
				/>
			)}

			<ModalsSubactions
				ref={modalsSubactionsRef} 
				onCallbackClose={() => onCallbackAddFunction()}
			/>

			<ModalRelateSprint 
				ref={modalSRelateSprint} 
				onCallbackSubmit={() => {
					if(callBackChangeActionSprint)
						callBackChangeActionSprint();
				}}			
			/>

			<ModalsSubactions
				ref={modalsSubactionsRef} 
				onCallbackClose={() => onCallbackAddFunction()}
			/>

			<ModalAddChangeDelete 
				ref={modalsRef} 
				onCallbackDelete={onCallbackDeleteFunction}
			/>

			<CommentsModal ref={modalCommentsRef} />

			<ModalHistory ref={modalHistoryRef} />

			<ModalProgress ref={modalProgressRef} />

			<ModalPostponement ref={modalPostponementRef} />

			<ModalCancel ref={modalCancelRef} />

			<ModalEfficiency ref={modalEfficiencyRef} />

			<ModalShowPostponements ref={modalShowPostponements} />
		</>
	);
};

export default forwardRef(ListOfActions);
